import { FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogbookDiet,
  useGetDietsQuery,
} from "../../logbook-diets/api/logbookDietApi";

type CreateDietForm = {
  title: string;
  start_date: string;
  end_date: string;
  notes?: string;
};

type DietStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "active"
  | "assessment_ongoing"
  | "closed";

function Badge({ text }: { text: string }) {
  const cls =
    text === "pending"
      ? "bg-yellow-50 text-yellow-700 border-yellow-200"
      : text === "approved"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : text === "rejected"
      ? "bg-red-50 text-red-700 border-red-200"
      : text === "active"
      ? "bg-green-50 text-green-700 border-green-200"
      : text === "assessment_ongoing"
      ? "bg-purple-50 text-purple-700 border-purple-200"
      : text === "closed"
      ? "bg-gray-100 text-gray-700 border-gray-200"
      : "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs rounded-full border ${cls}`}
    >
      {text}
    </span>
  );
}

function asStatus(x: any): DietStatus {
  const s = String(x || "").toLowerCase();
  if (
    s === "pending" ||
    s === "approved" ||
    s === "rejected" ||
    s === "active" ||
    s === "assessment_ongoing" ||
    s === "closed"
  )
    return s;
  return "pending";
}

export default function NecProbationerApplicationList() {
  const { data = [], isLoading, error, refetch } = useGetDietsQuery();

  const sorted = useMemo(() => {
    return [...data].sort((a: LogbookDiet, b: LogbookDiet) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [data]);

  // ✅ status → actions
  const actionsFor = (status: DietStatus) => {
    return {
      canApprove: status === "pending",
      canReject: status === "pending",
      canDelete: status === "pending",
      canActivate: status === "approved",
      canStart: status === "active",
      canClose: status === "assessment_ongoing",
      canExtend: status === "active" || status === "assessment_ongoing",
    };
  };

  const renderActions = (d: LogbookDiet) => {
    const status = asStatus((d as any).status);
    const a = actionsFor(status);

    return (
      <div className="flex flex-wrap gap-2">
        {/* Always view */}

        <Link
          to={`/probationer-applications/nec/submissions/diets/${d.id}/categories`}
          state={{ dietId: d.id, dietTitle: d.title }}
          className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
        >
          View
        </Link>
      </div>
    );
  };

  return (
    <div className="bg-white border rounded-xl p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">
            Membership Application Submissions by DIets{" "}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            This section holds only submissions assigned to you.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="mt-6 text-gray-600">Loading diets...</div>
      ) : error ? (
        <div className="mt-6 text-red-600">Failed to load diets.</div>
      ) : (
        <>
          {/* Desktop */}
          <div className="mt-6 hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Title</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Dates</th>
                  <th className="py-2">Active</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>

              <tbody>
                {sorted.map((d) => (
                  <tr key={d.id} className="border-b">
                    <td className="py-3">
                      <div className="font-semibold text-gray-800">
                        {d.title}
                      </div>
                      <div className="text-xs text-gray-500">{d.notes}</div>
                    </td>

                    <td className="py-3">
                      <Badge text={(d as any).status} />
                    </td>

                    <td className="py-3 text-gray-700">
                      {d.start_date} → {d.end_date}
                    </td>

                    <td className="py-3 text-gray-700">
                      {d.is_active ? "Yes" : "No"}
                    </td>

                    <td className="py-3">{renderActions(d)}</td>
                  </tr>
                ))}

                {!sorted.length && (
                  <tr>
                    <td colSpan={5} className="py-6 text-gray-500">
                      No diets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="mt-6 md:hidden space-y-3">
            {sorted.map((d) => (
              <div key={d.id} className="border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800 truncate">
                      {d.title}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{d.id}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {d.start_date} → {d.end_date}
                    </div>
                  </div>
                  <Badge text={(d as any).status} />
                </div>

                <div className="mt-3">{renderActions(d)}</div>
              </div>
            ))}

            {!sorted.length && (
              <div className="text-gray-500">No diets found.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
